/**
 * Sign In page - Brutalist minimal design
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStep, setResetStep] = useState<'request' | 'confirm'>('request');
  const [successMessage, setSuccessMessage] = useState('');
  const { signIn, resetPassword, confirmResetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      await resetPassword(email);
      setResetStep('confirm');
      setSuccessMessage('Password reset code sent to your email');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      await confirmResetPassword(email, resetCode, newPassword);
      setSuccessMessage('Password reset successful! You can now sign in with your new password.');
      setTimeout(() => {
        setShowResetPassword(false);
        setResetStep('request');
        setResetCode('');
        setNewPassword('');
        setSuccessMessage('');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password View
  if (showResetPassword) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 md:px-6 py-8 md:py-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 md:mb-12 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Reset Password</h1>
            <p className="text-text-secondary font-mono text-xs md:text-sm">
              {resetStep === 'request'
                ? 'Enter your email to receive a reset code'
                : 'Enter the code sent to your email'
              }
            </p>
          </div>

          {/* Form Card */}
          <div className="card">
            {resetStep === 'request' ? (
              <form onSubmit={handleResetPasswordRequest} className="space-y-6">
                {/* Email Field */}
                <div>
                  <label className="block text-xs font-mono text-text-muted mb-2">
                    EMAIL *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input"
                    placeholder="you@company.com"
                  />
                </div>

                {/* Success Message */}
                {successMessage && (
                  <div className="p-3 bg-green-950/20 border border-green-900/50 rounded text-green-400 text-sm">
                    {successMessage}
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-950/20 border border-red-900/50 rounded text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                  >
                    {loading ? 'Sending code...' : 'Send Reset Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(false)}
                    className="btn-secondary w-full"
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPasswordConfirm} className="space-y-6">
                {/* Email Display */}
                <div>
                  <label className="block text-xs font-mono text-text-muted mb-2">
                    EMAIL
                  </label>
                  <div className="input bg-background-dim text-text-muted">
                    {email}
                  </div>
                </div>

                {/* Reset Code Field */}
                <div>
                  <label className="block text-xs font-mono text-text-muted mb-2">
                    RESET CODE *
                  </label>
                  <input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    className="input font-mono"
                    placeholder="123456"
                  />
                </div>

                {/* New Password Field */}
                <div>
                  <label className="block text-xs font-mono text-text-muted mb-2">
                    NEW PASSWORD *
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="input"
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-text-muted mt-2 font-mono">
                    Min 8 chars, uppercase, lowercase, number, special character
                  </p>
                </div>

                {/* Success Message */}
                {successMessage && (
                  <div className="p-3 bg-green-950/20 border border-green-900/50 rounded text-green-400 text-sm">
                    {successMessage}
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-950/20 border border-red-900/50 rounded text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full"
                  >
                    {loading ? 'Resetting password...' : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(false);
                      setResetStep('request');
                      setResetCode('');
                      setNewPassword('');
                    }}
                    className="btn-secondary w-full"
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 md:px-6 py-8 md:py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 md:mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Sign In</h1>
          <p className="text-text-secondary font-mono text-xs md:text-sm">
            Access your permitting dashboard
          </p>
        </div>

        {/* Form Card */}
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-xs font-mono text-text-muted mb-2">
                EMAIL *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-xs font-mono text-text-muted">
                  PASSWORD *
                </label>
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="text-xs text-white hover:underline font-mono"
                >
                  Forgot?
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input"
                placeholder="••••••••"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-950/20 border border-red-900/50 rounded text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Sign Up Link */}
        <p className="mt-6 text-center text-sm text-text-secondary">
          Don't have an account?{' '}
          <Link to="/signup" className="text-white hover:underline font-medium">
            Sign Up
          </Link>
        </p>
      </div>
    </div>
  );
};
